import { useCallback, useRef } from 'react';

/**
 * Backdrop-dismiss guard for custom (non-Radix) modal overlays.
 *
 * Returns pointer-event handlers to spread on the overlay element (the dark
 * full-screen backdrop). The modal closes only when ALL of these hold:
 *
 *   1. the press (pointerdown) landed on the backdrop itself, AND
 *   2. the release (pointerup) also landed on the backdrop, AND
 *   3. nothing is in flight (`isBusy` is false).
 *
 * WHY both ends, not a plain onClick: a `click` event collapses its target to
 * the common ancestor of the press and release elements. So a drag that starts
 * inside the modal (selecting text in a field, or pressing X then changing your
 * mind) and releases on the backdrop fires `click` on the backdrop and wrongly
 * closes the modal — losing unsaved input. Tracking pointerdown + pointerup
 * separately lets us require BOTH ends to be the backdrop itself.
 *
 * WHY pointer events, not mouse events: `mousedown`/`mouseup` synthesized from a
 * touch both report the RELEASE position, so a touch-drag from inside the modal
 * out to the backdrop would set the press flag at release and close the modal —
 * re-introducing the very bug on mobile. `pointerdown`/`pointerup` fire at the
 * true press and release positions for mouse, touch, and pen alike.
 *
 * Usage:
 *   const backdrop = useBackdropDismiss(onClose, submitting);
 *   return <div className="overlay" {...backdrop}>…</div>;
 *
 * IMPORTANT (rules of hooks): call this BEFORE any early return such as
 * `if (!isOpen) return null;` — a hook must run on every render.
 *
 * @param {() => void} onClose  close action invoked on a clean backdrop click
 * @param {boolean}    isBusy   when true, backdrop clicks are ignored so an
 *                              in-flight save/delete is never dismissed
 * @returns {{ onPointerDown: (e: React.PointerEvent) => void,
 *             onPointerUp: (e: React.PointerEvent) => void }}
 */
export default function useBackdropDismiss(onClose, isBusy = false) {
  const pressedOnBackdropRef = useRef(false);

  const onPointerDown = useCallback((e) => {
    // Record whether the press landed on the backdrop itself (not a child).
    pressedOnBackdropRef.current = e.target === e.currentTarget;
  }, []);

  const onPointerUp = useCallback(
    (e) => {
      const pressedOnBackdrop = pressedOnBackdropRef.current;
      pressedOnBackdropRef.current = false; // reset for the next gesture
      if (pressedOnBackdrop && e.target === e.currentTarget && !isBusy) {
        onClose?.();
      }
    },
    [onClose, isBusy],
  );

  return { onPointerDown, onPointerUp };
}
